import { useDispatch } from 'react-redux'
import { useMutation, MutationFunctionOptions } from '@apollo/client'
import {
  MutationRegisterUserArgs,
  Patient,
  ProfileInformation,
  UserRegistrationInput,
} from '@babylon/consumer-api/types'

import { useUserId } from '@/redux/userProfile/selectors'
import registerUserMutation from '@/mutations/registerUser'
import UpdatePatientMutation from '@/mutations/updateUser'
import { setMembershipCode } from '@/redux/userConfig/actions'
import { setUserEligibility } from '@/redux/userProfile/actions'
import { RegistrationFormInput } from '@/types'
import { REDEMPTION_TYPE } from '@/constants'
import { getStateCodeFromPostalcode } from '@/utils'
import { useRegionInfo } from '@/redux/regionInfo/selectors'

type RegisterUserResponse = {
  registerUser: Patient
}

const userRegistrationFields: (keyof UserRegistrationInput)[] = [
  'first_name',
  'last_name',
  'date_of_birth',
  'gender',
  'email',
  'password',
  'region_id',
  'postcode',
  'agreedTermsAndConditions',
  'promocode',
  'phone_country_code',
  'phone_number',
  'accepted_notice_versions',
  'redemptions_attributes',
  'address_state_code',
]

const userUpdateFields: (keyof ProfileInformation)[] = [
  'first_name',
  'last_name',
  'date_of_birth',
  'gender',
  'phone_country_code',
  'phone_number',
  'address_state_code',
  'address_post_code',
]

const stripNonAcceptedFields = (
  payload: UserRegistrationInput,
  acceptedFields: any
): UserRegistrationInput => {
  const payloadKeys = Object.keys(payload) as (keyof UserRegistrationInput)[]
  const strippedDownObject: UserRegistrationInput = { ...payload }

  payloadKeys.forEach((key) => {
    if (!acceptedFields.includes(key) || !strippedDownObject[key]) {
      delete strippedDownObject[key]
    }
  })

  return strippedDownObject
}

const useRegisterUser = () => {
  const userId = useUserId()
  const { iso_code: isoCode } = useRegionInfo()

  const dispatch = useDispatch()
  const [registerUser] = useMutation<
    RegisterUserResponse,
    MutationRegisterUserArgs
  >(registerUserMutation)

  const [updateUser] = useMutation<RegisterUserResponse>(UpdatePatientMutation)

  const doRegisterUser = async (
    payload: RegistrationFormInput,
    membershipCode: string
  ) => {
    const {
      agreedTAndCs = [],
      agreedPrivacyPolicy = [],
      agreedExplicitConsent = [],
      agreedMarketing = [],
      agreedHIPAAAuthorization = [],
      agreedConsumerNetworkNotice = [],
      ...rest
    } = payload

    const acceptedNoticeVersions = [
      ...agreedTAndCs,
      ...agreedPrivacyPolicy,
      ...agreedExplicitConsent,
      ...agreedMarketing,
      ...agreedHIPAAAuthorization,
      ...agreedConsumerNetworkNotice,
    ].filter(Boolean)

    const patient = stripNonAcceptedFields(
      rest as UserRegistrationInput,
      userRegistrationFields
    )

    let addressStateCode

    if (isoCode === 'USA') {
      const { state } = await getStateCodeFromPostalcode(
        payload.address_post_code ?? ''
      )
      addressStateCode = state
    }

    const registerUserParams: MutationFunctionOptions<
      RegisterUserResponse,
      MutationRegisterUserArgs
    > = {
      context: {
        credentials: 'omit',
        showFeedback: false,
      },
      variables: {
        agreed_terms_and_conditions: 1,
        patient: {
          ...patient,
          ...(isoCode === 'USA' && {
            date_of_birth: payload.date_of_birth_us || '',
          }),
          ...(!!addressStateCode && {
            address_state_code: addressStateCode,
          }),
          postcode: payload.address_post_code,
          accepted_notice_versions: acceptedNoticeVersions,
          ...(!!membershipCode && {
            redemptions_attributes: [
              {
                code: membershipCode,
                redemption_type: REDEMPTION_TYPE,
              },
            ],
          }),
        },
      },
    }

    // Reset User Eligibility for every submission
    dispatch(setUserEligibility(true))
    const result = await registerUser(registerUserParams)

    dispatch(setMembershipCode(membershipCode))

    return result
  }

  const doUpdateUser = async (payload: RegistrationFormInput) => {
    const patient = stripNonAcceptedFields(
      payload as UserRegistrationInput,
      userUpdateFields
    )
    await updateUser({
      context: {
        showFeedback: false,
      },
      variables: {
        patient: {
          ...patient,
        },
        id: userId,
      },
    })
  }

  return {
    registerUser: doRegisterUser,
    updateUser: doUpdateUser,
  }
}

export default useRegisterUser
export { stripNonAcceptedFields, userUpdateFields }
